import debounce from 'lodash.debounce';
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { useAuth } from '@gbs-monorepo-packages/auth';
import {
  BreadcrumbsComponent,
  DEBOUNCE_TIME,
  DefaultDescriptionBenefitEducation,
  ETypeFolder,
  FormModal,
  HighUserRoles,
  type IApiThrowsError,
  type IFolderDTO,
  INIT_PAGE,
  LIMIT_PAGE,
  Logger,
  PageContent,
  PageHeader,
  SearchBar,
  Tab,
  formatUnknownErrorMessage,
  getRouteFrom,
  useBreadcrumbs,
  useCanDocuments,
  useToast,
} from '@gbs-monorepo-packages/common';

import { ListFolder } from '../../components/ListFolder';
import { FOLDER, FOLDERS } from '../../constants/RoutePaths';
import { useClients } from '../../hooks/useCompanies';
import { useFolder } from '../../hooks/useFolder';
import {
  type ICreateFolderProps,
  type IPaginationFolderDTO,
  type IUpdateFolderProps,
} from '../../services/folders';
import {
  getClientBrandingName,
  getPrivateResourcesLabel,
} from '../../utils/agencyBranding';
import { AddFolderModal } from './components/AddFolder';
import { EditFolderModal } from './components/EditFolder';
import { MainContainer, SearchContainer } from './styles';

const DocumentHeaderColumns = [
  {
    id: 'name',
    name: 'Description',
    textAlign: 'start',
  },
  {
    id: 'updatedAt',
    name: 'Last Upload',
    textAlign: 'end',
  },
  {
    id: 'options',
    name: '',
    textAlign: 'center',
  },
];

export const FoldersDocuments = (): JSX.Element => {
  const { typeFolder = '', clientId = '' } = useParams();
  const { selectedClient } = useClients();
  const privateResourcesLabel = getPrivateResourcesLabel(
    getClientBrandingName(selectedClient)
  );
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const folderRoute = getRouteFrom(FOLDER);
  const { user } = useAuth();

  const {
    breadcrumbs,
    addBreadcrumb,
    removeBreadcrumb,
    clearBreadcrumbs,
    updateBreadcrumb,
  } = useBreadcrumbs();

  const {
    loadingFolders,
    getListFolderByClient,
    folders,
    setFolders,
    paginationMeta,
    handleSetSortOrder,
    sortOrder,
    createFolder,
    updateFolder,
    deleteFolder,
  } = useFolder();

  const constTypeFolder =
    typeFolder === 'private' ? ETypeFolder.PRIVATE : ETypeFolder.PUBLIC;
  const TitlePageText =
    typeFolder === 'private'
      ? privateResourcesLabel
      : 'Public Employee Resources';

  const [loadingActions, setLoadingActions] = useState(false);
  const [search, setSearch] = useState('');
  const lastSearch = useRef(search);

  const { addToast } = useToast();

  const [folderToEdit, setFolderEdit] = useState<IFolderDTO | null>(null);
  const [folderDelete, setFolderDelete] = useState<IFolderDTO | null>(null);
  const [openAddFolderModal, setOpenAddFolderModal] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const isModalDeleteOpen = !!folderDelete;
  const userIsHighRole = useMemo(() => {
    return HighUserRoles.some((role) => user?.roles.includes(role));
  }, [user]);

  const { allowCreate } = useCanDocuments({
    userId: user?.id,
    userIsHighRole,
    folderData: {
      typeFolder: constTypeFolder,
    },
  });

  const defaultFolderId = useMemo(
    () => selectedClient?.defaultLmsFolder.id,
    [selectedClient]
  );

  const toRouteSubFolder = useCallback(
    (folderId: number | string) =>
      generatePath(folderRoute, {
        clientId,
        typeFolder,
        folderId,
      }),
    [clientId, folderRoute, typeFolder]
  );

  const handleRowClick = useCallback(
    (item: IFolderDTO) => {
      const route = toRouteSubFolder(item.id);
      addBreadcrumb({
        name: item.name,
        url: route,
      });
      navigate(route, { state: { from: pathname } });
    },
    [addBreadcrumb, navigate, pathname]
  );

  const handleSetIsDropdownOpen = useCallback((isOpen: boolean) => {
    setIsDropdownOpen(isOpen);
  }, []);

  const handleSelectFolderEdit = useCallback(
    (item: IFolderDTO) => {
      setFolderEdit(item);
    },
    [setFolderEdit]
  );

  const handleSelectFolderDelete = useCallback(
    (item: IFolderDTO) => {
      setFolderDelete(item);
    },
    [setFolderDelete]
  );

  const foldersRequestIdRef = useRef(0);

  const getFolders = useCallback(
    async (
      clientId: string,
      page: number,
      limit: number,
      filter?: string,
      redirectToDefaultPublicFolder = false
    ) => {
      const requestId = ++foldersRequestIdRef.current;

      await getListFolderByClient({
        clientId: clientId,
        page,
        limit,
        filter: filter ?? '',
        search: String(constTypeFolder),
        sort: JSON.stringify(sortOrder),
      })
        .then((response?: IPaginationFolderDTO) => {
          if (requestId !== foldersRequestIdRef.current) {
            return;
          }

          if (response) {
            if (
              redirectToDefaultPublicFolder &&
              defaultFolderId != null &&
              constTypeFolder === ETypeFolder.PUBLIC
            ) {
              const defaultFolder = response.data.find(
                (folder) => folder.id === defaultFolderId
              );
              if (defaultFolder) {
                addBreadcrumb({
                  name: defaultFolder.name,
                  url: toRouteSubFolder(defaultFolder.id),
                });
                navigate(toRouteSubFolder(defaultFolder.id), {
                  state: { from: pathname },
                });
              }
            }
          }
        })
        .catch((error: IApiThrowsError) => {
          if (requestId !== foldersRequestIdRef.current) {
            return;
          }

          Logger.debug('error: ', error);
          addToast({
            title: 'Error on load folders',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
            duration: 3000,
          });
        });
    },
    [
      addBreadcrumb,
      addToast,
      constTypeFolder,
      defaultFolderId,
      getListFolderByClient,
      navigate,
      pathname,
      sortOrder,
      toRouteSubFolder,
    ]
  );

  useEffect(() => {
    setSearch('');
    clearBreadcrumbs();

    return () => {
      foldersRequestIdRef.current += 1;
    };
  }, [typeFolder, clientId]);

  useEffect(() => {
    if (!Array.isArray(breadcrumbs) || breadcrumbs.length !== 0) {
      return;
    }

    void getFolders(clientId ?? 0, INIT_PAGE, LIMIT_PAGE, '', true);
  }, [breadcrumbs, clientId, getFolders, typeFolder]);

  useEffect(() => {
    if (!Array.isArray(breadcrumbs) || breadcrumbs.length === 0) {
      addBreadcrumb({
        name: TitlePageText,
        url: pathname,
      });
      return;
    }

    if (breadcrumbs[0]?.name !== TitlePageText) {
      updateBreadcrumb(0, { name: TitlePageText });
    }
  }, [TitlePageText, addBreadcrumb, breadcrumbs, pathname, updateBreadcrumb]);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (search.trim() !== lastSearch.current) {
      const execSearch = () => {
        lastSearch.current = search.trim();
        setFolders([]);
        searchFolder(true);
      };

      if (search.trim() === '') {
        execSearch();
      } else {
        timer = setTimeout(execSearch, DEBOUNCE_TIME);
      }
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [search]);

  useEffect(() => {
    const debouncedGetFolders = debounce(() => {
      if (sortOrder) {
        void getFolders(clientId ?? 0, INIT_PAGE, LIMIT_PAGE, search);
      }
    }, DEBOUNCE_TIME);

    debouncedGetFolders();

    return () => {
      debouncedGetFolders.cancel();
    };
  }, [clientId, getFolders, search, sortOrder]);

  const searchFolder = (searchByButton = false) => {
    if (!loadingFolders || searchByButton) {
      const pageAux = searchByButton
        ? 0
        : Number((paginationMeta?.page ?? 0) > 0 ? paginationMeta?.page : 0);
      getListFolderByClient({
        clientId: String(clientId),
        page: pageAux + 1,
        limit: LIMIT_PAGE,
        filter: search ?? '',
        search: String(constTypeFolder),
        sort: JSON.stringify(sortOrder),
      }).catch((error: IApiThrowsError) => {
        Logger.debug('error: ', error);
        addToast({
          title: 'Error on load folders',
          description: formatUnknownErrorMessage(
            error,
            DefaultDescriptionBenefitEducation
          ),
          styleType: 'error',
          dataCy: 'error-toast',
          duration: 3000,
        });
      });
    }
  };

  const addFolder = useCallback(
    async (folder: ICreateFolderProps): Promise<boolean> => {
      let result = false;
      if (!loadingActions) {
        setLoadingActions(true);

        await createFolder(folder)
          .then(() => {
            addToast({
              title: 'Folder created',
              styleType: 'success',
              dataCy: 'success-toast',
              duration: 3000,
            });
            setOpenAddFolderModal(false);
            void getFolders(
              clientId ?? 0,
              INIT_PAGE,
              LIMIT_PAGE,
              lastSearch.current
            );
            result = true;
          })
          .catch((error: IApiThrowsError) => {
            addToast({
              title: 'Error on creating folder',
              description: formatUnknownErrorMessage(
                error,
                DefaultDescriptionBenefitEducation
              ),
              styleType: 'error',
              dataCy: 'error-toast',
              duration: 3000,
            });
          })
          .finally(() => {
            setLoadingActions(false);
          });
      }

      return result;
    },
    [addToast, getFolders, loadingActions]
  );

  const editFolder = useCallback(
    async (folder: IUpdateFolderProps): Promise<boolean> => {
      let result = false;
      if (!loadingActions) {
        setLoadingActions(true);

        await updateFolder(folder)
          .then(() => {
            addToast({
              title: 'Folder updated',
              styleType: 'success',
              dataCy: 'success-toast',
              duration: 3000,
            });
            void getFolders(
              clientId ?? 0,
              INIT_PAGE,
              LIMIT_PAGE,
              lastSearch.current
            );
            result = true;
          })
          .catch((error: IApiThrowsError) => {
            addToast({
              title: 'Error on updated folder',
              description: formatUnknownErrorMessage(
                error,
                DefaultDescriptionBenefitEducation
              ),
              styleType: 'error',
              dataCy: 'error-toast',
              duration: 3000,
            });
          })
          .finally(() => {
            setLoadingActions(false);
          });
      }

      return result;
    },
    [addToast, getFolders, loadingActions]
  );

  const removeFolder = useCallback(() => {
    if (!folderDelete) {
      return;
    }

    if (!loadingActions) {
      setLoadingActions(true);

      deleteFolder(folderDelete.id)
        .then(() => {
          addToast({
            title: 'Folder deleted',
            styleType: 'success',
            dataCy: 'success-toast',
            duration: 3000,
          });
          void getFolders(
            clientId ?? 0,
            INIT_PAGE,
            LIMIT_PAGE,
            lastSearch.current
          );
        })
        .catch((error: IApiThrowsError) => {
          addToast({
            title: 'Error on deleting folder',
            description: formatUnknownErrorMessage(
              error,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
            duration: 3000,
          });
        })
        .finally(() => {
          setFolderDelete(null);
          setLoadingActions(false);
        });
    }
  }, [addToast, folderDelete, getFolders, loadingActions]);

  const handleDecline = useCallback(() => {
    setFolderEdit(null);
  }, []);

  const handleDeclineDelete = useCallback(() => {
    setFolderDelete(null);
  }, []);

  const removeBreadcrumbPage = useCallback(
    (index: number) => {
      removeBreadcrumb(index);
    },
    [removeBreadcrumb]
  );

  const hasRequestInLoading = useMemo(() => {
    return loadingFolders || loadingActions;
  }, [loadingFolders, loadingActions]);

  const foldersRoute = getRouteFrom(FOLDERS);

  const handleTabChange = useCallback(
    (value: string) => {
      if (value) {
        setSearch('');
        navigate(generatePath(foldersRoute, { clientId, typeFolder: value }));
      }
    },
    [navigate, clientId, foldersRoute]
  );

  const handleAddFolder = useCallback(() => {
    setOpenAddFolderModal(true);
  }, []);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearch('');
  }, []);

  return (
    <MainContainer data-cy="document-container">
      <PageHeader
        title="Resources"
        buttonText={allowCreate ? 'Add Folder' : ''}
        onClick={handleAddFolder}
        disabledButton={hasRequestInLoading}
      />

      <PageContent
        value={typeFolder}
        tabs={
          <>
            <Tab dataCy="tab-private" value="private">
              {privateResourcesLabel}
            </Tab>
            <Tab dataCy="tab-public" value="public">
              Public Employee Resources
            </Tab>
          </>
        }
        defaultTabValue={typeFolder}
        onTabValueChange={handleTabChange}
      >
        <SearchContainer>
          <SearchBar
            onChange={handleSearchChange}
            onClearInput={handleClearSearch}
            search={search}
            maxLength={50}
            loading={loadingFolders}
            id="search-folder"
            dataCy="input-searchFolder"
          />
        </SearchContainer>
        <BreadcrumbsComponent
          links={breadcrumbs}
          onClick={removeBreadcrumbPage}
        />
        <ListFolder
          folders={folders}
          loading={loadingFolders}
          paginationMeta={paginationMeta}
          searchFolder={searchFolder}
          setSortOrder={handleSetSortOrder}
          sortOrder={sortOrder}
          headerColumns={DocumentHeaderColumns}
          disabledActions={false}
          handleFolderClick={handleRowClick}
          handleSelectEdit={handleSelectFolderEdit}
          handleSelectDelete={handleSelectFolderDelete}
          handleSetIsDropdownOpen={handleSetIsDropdownOpen}
        />
      </PageContent>
      <AddFolderModal
        typeFolder={constTypeFolder}
        onAccept={addFolder}
        onDecline={() => {
          setOpenAddFolderModal(false);
        }}
        open={!isDropdownOpen && openAddFolderModal}
        loading={loadingActions}
        zIndex={10}
      />

      {folderToEdit && (
        <EditFolderModal
          folder={folderToEdit}
          onAccept={editFolder}
          onDecline={handleDecline}
          open={!isDropdownOpen && folderToEdit !== null}
          loading={loadingActions}
          zIndex={10}
        />
      )}

      {folderDelete && (
        <FormModal
          dataCy="delete-folder-dialog"
          acceptText="Confirm"
          declineText="Cancel"
          open={!isDropdownOpen && isModalDeleteOpen}
          loading={loadingActions}
          mainText={`Are you sure you want to delete ${folderDelete.name} ?`}
          onAccept={removeFolder}
          onDecline={handleDeclineDelete}
          onOpenChange={handleDeclineDelete}
          zIndex={10}
        />
      )}
    </MainContainer>
  );
};
